import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import {
  DeAvatarCellComponent,
  DeBadgeCellComponent,
  DeColumnTemplateDirective,
  DocumentExplorerComponent,
  avatarColumn,
  badgeColumn,
  normalize,
  type AvatarOptions,
  type BadgeOptions,
  type DocumentItem,
  type ExplorerView,
} from '@document-explorer/angular';
import type { ColumnDef } from '@document-explorer/angular';
import { apiMapper, apiResponse, type ApiDocument } from './data.js';

type Preset = 'simple' | 'configured' | 'custom';
type AvatarDisplayMode = 'both' | 'avatar' | 'name';

/**
 * Stand-in avatar images, inlined as data URIs so the demo works offline. A
 * real app would use whatever URL its API returns; `image` accepts a dot path
 * or a function, so either works.
 */
const AVATAR_IMAGES: Record<string, string> = {
  'Nitesh':
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCA0MCI+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJnIiB4MT0iMCIgeTE9IjAiIHgyPSIxIiB5Mj0iMSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzYzNjZmMSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzBlYTVlOSIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0idXJsKCNnKSIvPjx0ZXh0IHg9IjIwIiB5PSIyNiIgZm9udC1mYW1pbHk9InN5c3RlbS11aSxzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iNjAwIiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5OPC90ZXh0Pjwvc3ZnPg==',
  'Ada Lovelace':
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCA0MCI+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJnIiB4MT0iMCIgeTE9IjAiIHgyPSIxIiB5Mj0iMSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI2Y5NzMxNiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iI2RiMjc3NyIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0idXJsKCNnKSIvPjx0ZXh0IHg9IjIwIiB5PSIyNiIgZm9udC1mYW1pbHk9InN5c3RlbS11aSxzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iNjAwIiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5BTDwvdGV4dD48L3N2Zz4=',
  'Grace Hopper':
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCA0MCI+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJnIiB4MT0iMCIgeTE9IjAiIHgyPSIxIiB5Mj0iMSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzA1OTY2OSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzE0YjhhNiIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0idXJsKCNnKSIvPjx0ZXh0IHg9IjIwIiB5PSIyNiIgZm9udC1mYW1pbHk9InN5c3RlbS11aSxzYW5zLXNlcmlmIiBmb250LXNpemU9IjE2IiBmb250LXdlaWdodD0iNjAwIiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5HSDwvdGV4dD48L3N2Zz4=',
};


/**
 * The Angular twin of the React demo — same fixture, same presets, same
 * controls. If the two behave differently on this data, that is a bug in
 * `@document-explorer/core`, not in either renderer.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    DocumentExplorerComponent,
    DeColumnTemplateDirective,
    DeAvatarCellComponent,
    DeBadgeCellComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <main class="page">
      <header class="page-header">
        <div>
          <h1>Document Explorer — Angular</h1>
          <p class="lede">
            The same awkward API payload as the React example — snake_case keys, numeric ids, a
            nested children block, an orphan and a duplicate id — rendered without transforming the
            data first.
          </p>
        </div>
        <button type="button" class="toggle" (click)="dark.set(!dark())">
          {{ dark() ? 'Light theme' : 'Dark theme' }}
        </button>
      </header>

      <nav class="tabs" aria-label="Example presets">
        @for (tab of tabs; track tab.id) {
          <button
            type="button"
            class="tab"
            [attr.aria-pressed]="preset() === tab.id"
            (click)="preset.set(tab.id)"
          >
            {{ tab.label }}
          </button>
        }
      </nav>

      <section class="demo">
        @switch (preset()) {
          @case ('simple') {
            <pre class="snippet">&lt;doc-explorer [data]="apiResponse" [mapper]="apiMapper" /&gt;</pre>
            <doc-explorer
              [data]="data"
              [mapper]="mapper"
              [view]="view()"
              (viewChange)="view.set($event)"
              (folderOpen)="note('Opened folder: ' + ($event?.name ?? 'Home'))"
              (documentOpen)="note('Opened document: ' + $event.name)"
            />
          }

          @case ('configured') {
            <pre class="snippet">{{ configuredSnippet }}</pre>
            <doc-explorer
              [data]="data"
              [mapper]="mapper"
              title="Documents"
              [search]="globalSearch"
              [selection]="multiSelectFilesOnly"
              [actions]="viewAndDownload"
              [view]="view()"
              (viewChange)="view.set($event)"
              (folderOpen)="note('Opened folder: ' + ($event?.name ?? 'Home'))"
              (documentOpen)="note('Opened document: ' + $event.name)"
              (documentView)="note('View: ' + $event.name)"
              (documentDownload)="note('Download: ' + ($event.downloadUrl ?? $event.name))"
              (selectionChange)="selected.set($event)"
            />
          }

          @default {
            <pre class="snippet">{{ customSnippet }}</pre>
            <div class="controls">
              <span class="controls-label">Owner shows:</span>
              @for (mode of avatarModes; track mode.id) {
                <button
                  type="button"
                  class="chip"
                  [attr.aria-pressed]="avatarDisplay() === mode.id"
                  (click)="avatarDisplay.set(mode.id)"
                >
                  {{ mode.label }}
                </button>
              }
              <button
                type="button"
                class="chip"
                [attr.aria-pressed]="photos()"
                (click)="photos.set(!photos())"
              >
                {{ photos() ? '✓ Photos' : 'Photos' }}
              </button>
            </div>
            <p class="hint">
              <code>display</code> and <code>image</code> are independent — a photo still
              shows the name unless you also pick “Avatar only”, which moves the name to a
              hover tooltip and to screen readers. Drag a column edge to resize, or focus a
              handle and use <kbd>←</kbd> <kbd>→</kbd>; double-click (or <kbd>Enter</kbd>)
              resets it. Owner is capped at <code>maxWidth: 320</code>.
            </p>
            <doc-explorer
              [data]="data"
              [mapper]="mapper"
              title="Team documents"
              rootLabel="All documents"
              [search]="true"
              [views]="allViews"
              [selection]="true"
              [columns]="customColumns()"
              [actions]="customActions"
              [interaction]="openOnFileClick"
              [resizableColumns]="true"
              [view]="view()"
              (viewChange)="view.set($event)"
              (folderOpen)="note('Opened folder: ' + ($event?.name ?? 'Home'))"
              (action)="note($event.actionId + ': ' + $event.item.name)"
              (columnResize)="note('Resized ' + $event.key + ' to ' + $event.width + 'px')"
              (selectionChange)="selected.set($event)"
            >
              <!--
                The Angular equivalent of React's ColumnDef.render: one template
                per column key. <de-avatar-cell> draws exactly what React's
                AvatarCell draws, because both call resolveAvatar() in core.
              -->
              <ng-template deColumn="metadata.owner" let-item>
                <de-avatar-cell
                  [item]="item"
                  columnKey="metadata.owner"
                  [options]="avatarOptions()"
                />
              </ng-template>
              <ng-template deColumn="metadata.status" let-item>
                <de-badge-cell
                  [item]="item"
                  columnKey="metadata.status"
                  [options]="badgeOptions"
                />
              </ng-template>
            </doc-explorer>
          }
        }
      </section>

      <section class="panels">
        <div class="panel">
          <h2>Events</h2>
          @if (log().length === 0) {
            <p class="muted">Click a folder, open a document, or run an action.</p>
          } @else {
            <ul class="feed">
              @for (entry of log(); track $index) {
                <li>{{ entry }}</li>
              }
            </ul>
          }
        </div>

        <div class="panel">
          <h2>Selection ({{ selected().length }})</h2>
          @if (selected().length === 0) {
            <p class="muted">Nothing selected.</p>
          } @else {
            <ul class="feed">
              @for (item of selected(); track item.id) {
                <li>{{ item.name }} <span class="muted">({{ item.type }})</span></li>
              }
            </ul>
          }
        </div>

        <div class="panel">
          <h2>Normalizer warnings ({{ warnings.length }})</h2>
          <p class="muted">
            Malformed rows are repaired and reported, never thrown — one bad record must not take
            down the explorer.
          </p>
          <ul class="feed">
            @for (warning of warnings; track warning.code + warning.id) {
              <li><code>{{ warning.code }}</code> — {{ warning.message }}</li>
            }
          </ul>
        </div>
      </section>
    </main>
  `,
})
export class AppComponent {
  protected readonly data = apiResponse;
  protected readonly mapper = apiMapper as never;

  protected readonly tabs = [
    { id: 'simple' as const, label: 'Level 1 — one line' },
    { id: 'configured' as const, label: 'Level 2 — configured' },
    { id: 'custom' as const, label: 'Level 3 — customized' },
  ];

  protected readonly preset = signal<Preset>('configured');
  protected readonly view = signal<'list' | 'grid' | 'tree'>('list');
  protected readonly dark = signal(false);
  protected readonly log = signal<string[]>([]);
  protected readonly selected = signal<DocumentItem[]>([]);

  // Surface the warnings the normalizer collected, so the awkward rows in the
  // fixture are visibly handled rather than silently swallowed.
  protected readonly warnings = normalize(apiResponse, { mapper: apiMapper as never }).warnings;

  protected readonly allViews: ExplorerView[] = ['list', 'grid', 'tree'];
  protected readonly globalSearch = {
    scope: 'global' as const,
    searchableFields: ['metadata.owner'],
    placeholder: 'Search all documents',
  };
  protected readonly multiSelectFilesOnly = { mode: 'multiple' as const, folders: false };
  protected readonly viewAndDownload = { view: true, download: true };
  protected readonly openOnFileClick = { fileClick: 'open' as const };
  protected readonly avatarDisplay = signal<AvatarDisplayMode>('both');
  protected readonly photos = signal(false);

  protected readonly avatarModes = [
    { id: 'both' as const, label: 'Avatar + name' },
    { id: 'avatar' as const, label: 'Avatar only' },
    { id: 'name' as const, label: 'Name only' },
  ];

  /**
   * `display` and `image` are independent: `display` decides how much to show,
   * `image` decides whether the avatar is a photo or initials.
   */
  private readonly ownerColumn = computed(() =>
    avatarColumn({
      key: 'metadata.owner',
      label: 'Owner',
      display: this.avatarDisplay(),
      maxWidth: 320,
      ...(this.photos()
        ? { image: (item: DocumentItem) => AVATAR_IMAGES[item.metadata?.['owner'] as string] ?? '' }
        : {}),
    }),
  );

  /** Conventional tones with no configuration; `tones` would override them. */
  private readonly statusColumn = badgeColumn({ key: 'metadata.status', label: 'Status' });
  protected readonly badgeOptions: BadgeOptions = this.statusColumn.options;

  protected readonly avatarOptions = computed<AvatarOptions>(() => this.ownerColumn().options);

  protected readonly customColumns = computed<(ColumnDef | string)[]>(() => [
    'name',
    this.ownerColumn().column,
    this.statusColumn.column,
    'size',
    'updatedAt',
  ]);
  protected readonly customActions = [
    { id: 'share', label: 'Share' },
    { id: 'archive', label: 'Archive', danger: true, visible: (i: DocumentItem) => i.type === 'file' },
  ];

  protected readonly configuredSnippet = [
    '<doc-explorer',
    '  [data]="apiResponse"',
    '  [mapper]="apiMapper"',
    '  title="Documents"',
    "  [search]=\"{ scope: 'global', searchableFields: ['metadata.owner'] }\"",
    "  [selection]=\"{ mode: 'multiple', folders: false }\"",
    '  [actions]="{ view: true, download: true }"',
    '/>',
  ].join('\n');

  protected readonly customSnippet = [
    '<doc-explorer',
    "  [columns]=\"['name', ownerColumn, statusColumn, 'size']\"",
    '  [actions]="[shareAction, archiveAction]"',
    "  [views]=\"['list', 'grid', 'tree']\"",
    "  [interaction]=\"{ fileClick: 'open' }\"",
    '/>',
  ].join('\n');

  protected readonly isDark = computed(() => this.dark());

  constructor() {
    effect(() => {
      document.documentElement.setAttribute('data-de-theme', this.dark() ? 'dark' : 'light');
    });
  }

  protected note(message: string): void {
    this.log.update((current) => [message, ...current].slice(0, 6));
  }
}
